<!--
  O registo de uma ocorrência é longo: nove secções, e a maioria só faz sentido depois de se
  saber o que aconteceu. Antes, criar mostrava três secções e o resto só aparecia ao editar —
  quem já tinha a informação toda à mão tinha de gravar primeiro e voltar a entrar.

  Passou a um painel com separadores: abre-se na Caracterização, e quem quiser preenche já a
  análise, o tratamento ou a eficácia. Nada disso passa a ser obrigatório — o que obriga
  continua a ser o estado em que a ocorrência fica.
-->
<!--
  `<dialog>` aberto com showModal(): sobe à camada de topo do browser, acima de tudo o resto.
  Sem isso ficava preso ao contexto de empilhamento da `.route-view` (que o `view-transition-name`
  cria) e o botão de menu do telemóvel aparecia por cima do painel. De caminho, ganha-se o foco
  preso lá dentro e a tecla Esc.
-->
<dialog
  #dialog
  class="occ-form-dialog"
  [class.occ-form-dialog--docked]="minimized()"
  [attr.aria-label]="isEdit() ? 'Editar ocorrência' : 'Nova ocorrência'"
  (cancel)="onDialogCancel($event)"
  (click)="onDialogClick($event)"
>
<div
  class="occ-form-modal"
  [class.occ-form-modal--docked]="minimized()"
  [class.occ-form-modal--entry-done]="entryDone()"
  #surface
>

  <!-- Encostado, o painel continua inteiro por baixo (só escondido por CSS): destruí-lo levava
       consigo tudo o que estivesse por gravar. -->
  @if (minimized()) {
    <button class="occ-form-dock" type="button" (click)="toggleMinimize()">
      <span class="occ-form-dock-icon"><i class="fe fe-edit-3"></i></span>
      <span class="occ-form-dock-text">
        <span class="occ-form-dock-title">{{ isEdit() ? 'Editar ocorrência' : 'Nova ocorrência' }}</span>
        <span class="occ-form-dock-subtitle">
          {{ code() ?? 'Rascunho' }} · {{ filledPercent() }}% preenchido
        </span>
      </span>
      <span class="occ-form-dock-open"><i class="fe fe-maximize-2"></i></span>
    </button>
  }

  <header class="occ-form-head">
    <span class="avatar avatar-sm">
      <span class="avatar-title fs-lg rounded bg-primary-soft text-primary"><i class="fe fe-edit-3"></i></span>
    </span>
    <div class="occ-form-titles">
      <span class="occ-form-pretitle">{{ code() ?? 'Nova' }}</span>
      <h2 class="occ-form-title">{{ isEdit() ? 'Editar ocorrência' : 'Nova ocorrência' }}</h2>
    </div>

    <!-- Quanto está preenchido. Não é validação: é para se ver o que ainda dá para acrescentar. -->
    <div class="occ-form-progress" role="progressbar" [attr.aria-valuenow]="filledPercent()">
      <span class="occ-form-progress-track">
        <span class="occ-form-progress-fill" [style.width.%]="filledPercent()"></span>
      </span>
      <span class="occ-form-progress-value">{{ filledPercent() }}%</span>
      <span class="occ-form-progress-label">preenchido</span>
    </div>

    <button class="occ-form-close" type="button" aria-label="Minimizar" title="Minimizar"
            (click)="toggleMinimize()">
      <i class="fe fe-minus"></i>
    </button>
    <button class="occ-form-close" type="button" aria-label="Fechar" title="Fechar" (click)="requestClose()">
      <i class="fe fe-x"></i>
    </button>
  </header>

  <app-form-steps
    class="occ-form-steps"
    [steps]="steps()"
    [current]="step()"
    [states]="stepStates()"
    (select)="goToStep($event)"
  />

  <form [formGroup]="form" (ngSubmit)="submit()" class="occ-form-shell">
    <!-- A altura do corpo é escrita em pixéis pela diretiva, para poder ser animada: trocar de
         separador muda a altura do painel em vez de o fazer saltar. -->
    <div class="occ-form-body" [appAutoHeight]="surface" [appAutoHeightContent]="content">
      <div class="occ-form-content" #content>
      @if (isEdit() && step() === 'identificacao') {
    <div class="card">
      <div class="card-header">
        <div class="d-flex align-items-center">
          <span class="avatar avatar-sm me-3"><span class="avatar-title fs-lg rounded bg-primary-soft text-primary"><i class="fe fe-shuffle"></i></span></span>
          <h4 class="card-header-title mb-0">Identificação</h4>
        </div>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label">Situação</label>
            <select class="form-select" formControlName="situation" data-no-filter>
              @for (option of situationOptions; track option.value) {
                <option [ngValue]="option.value">{{ option.label }}</option>
              }
            </select>
          </div>
          <div class="col-md-6">
            <label class="form-label">Estado</label>
            <select class="form-select" formControlName="state">
              @for (option of stateChoices(); track option.value) {
                <option [ngValue]="option.value">{{ option.label }}</option>
              }
            </select>
          </div>
          @if (closing()) {
            <div class="col-md-6">
              <label class="form-label">Tipo de fecho <span class="text-danger">*</span></label>
              <select class="form-select" formControlName="closingType" data-no-filter>
                <option [ngValue]="null" disabled>Selecione…</option>
                @for (option of closingTypeOptions; track option.value) {
                  <option [ngValue]="option.value">{{ option.label }}</option>
                }
              </select>
              @if (form.controls.closingType.touched && form.controls.closingType.hasError('required')) {
                <small class="text-danger">Indique se o fecho foi com ou sem sucesso.</small>
              }
              @if (form.controls.closingType.errors?.['server']; as serverMessage) {
                <small class="text-danger">{{ serverMessage }}</small>
              }
            </div>
            <!-- O desfecho é de todos os fechos desde 2026-09-26, não só das denúncias e
                 reclamações. Esteve aqui dentro do @if das denúncias: numa oportunidade de
                 melhoria o campo não aparecia, o servidor exigia-o na mesma, e a ocorrência não
                 havia maneira de fechar — o separador ficava vermelho sem nada para corrigir. -->
            <div class="col-md-6">
              <label class="form-label">Desfecho <span class="text-danger">*</span></label>
              <select class="form-select" formControlName="outcome" data-no-filter>
                <option [ngValue]="null" disabled>Selecione…</option>
                @for (option of outcomeOptions; track option.value) {
                  <option [ngValue]="option.value">{{ option.label }}</option>
                }
              </select>
              @if (form.controls.outcome.touched && form.controls.outcome.hasError('required')) {
                <small class="text-danger">Escolha o desfecho do caso.</small>
              }
              @if (form.controls.outcome.errors?.['server']; as serverMessage) {
                <small class="text-danger">{{ serverMessage }}</small>
              }
            </div>
            <!-- Obrigatório só com o desfecho "Outro" (revisão de 2026-10-19). -->
            <div class="col-12">
              <label class="form-label">
                Resultado @if (closingResultRequired()) { <span class="text-danger">*</span> }
              </label>
              <textarea class="form-control" formControlName="closingResult" rows="3"
                        placeholder="O que ficou apurado ao fechar a ocorrência."></textarea>
              @if (form.controls.closingResult.touched && form.controls.closingResult.hasError('required')) {
                <small class="text-danger">Descreva o resultado com que a ocorrência foi fechada.</small>
              }
              @if (form.controls.closingResult.errors?.['server']; as serverMessage) {
                <small class="text-danger">{{ serverMessage }}</small>
              }
            </div>
            <div class="col-md-6">
              <label class="form-label">Data de conclusão <span class="text-muted small">(default hoje)</span></label>
              <app-date-picker
                formControlName="closingDate"
                [min]="form.controls.occurrenceDate.value"
                [max]="today"
              />
            </div>
            @if (isCaseWork()) {
              <div class="col-12">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" id="legal-hold" formControlName="legalHold" />
                  <label class="form-check-label" for="legal-hold">
                    Há processos judiciais, disciplinares ou administrativos pendentes
                  </label>
                </div>
                <small class="text-muted d-block">
                  A conservação é de três anos a contar do encerramento e, para além desse prazo,
                  enquanto estiverem pendentes processos relacionados.
                </small>
                @if (form.controls.legalHold.value) {
                  <input class="form-control mt-2" formControlName="legalHoldNote"
                         placeholder="Que processo mantém o caso em conservação" />
                }
              </div>
            }
          }
        </div>
      </div>
    </div>
      }
      @if (step() === 'caracterizacao') {
    <div class="card">
      <div class="card-header">
        <div class="d-flex align-items-center">
          <span class="avatar avatar-sm me-3"><span class="avatar-title fs-lg rounded bg-primary-soft text-primary"><i class="fe fe-tag"></i></span></span>
          <h4 class="card-header-title mb-0">Caracterização</h4>
        </div>
        <span class="badge bg-danger-soft">Obrigatória</span>
      </div>
      <div class="card-body">
        <div class="row g-3">

          <div class="col-md-6">
            <label class="form-label">Âmbito <span class="text-danger">*</span></label>
            <select class="form-select" formControlName="scope">
              <option [ngValue]="null" disabled>Selecione…</option>
              @for (option of scopeOptions; track option.value) { <option [ngValue]="option.value">{{ option.label }}</option> }
            </select>
            @if (form.controls.scope.touched && form.controls.scope.hasError('required')) {
              <small class="text-danger">O âmbito é obrigatório.</small>
            }
            @if (form.controls.scope.errors?.['server']; as serverMessage) {
              <small class="text-danger">{{ serverMessage }}</small>
            }
          </div>

          @if (showScopeOther()) {
            <div class="col-md-6">
              <label class="form-label">Identificar outro âmbito</label>
              <input class="form-control" formControlName="scopeOther" placeholder="Qual o âmbito?" />
              @if (form.controls.scopeOther.touched && form.controls.scopeOther.hasError('required')) {
                <small class="text-danger">Identifique o outro âmbito.</small>
              }
              @if (form.controls.scopeOther.errors?.['server']; as serverMessage) {
                <small class="text-danger">{{ serverMessage }}</small>
              }
            </div>
          }

          @if (showSubScope()) {
            <div class="col-md-6">
              <label class="form-label">Subâmbito</label>
              <select class="form-select" formControlName="subScope">
                <option [ngValue]="null" disabled>Selecione…</option>
                @for (option of subScopeOptions(); track option.value) { <option [ngValue]="option.value">{{ option.label }}</option> }
              </select>
              @if (form.controls.subScope.touched && form.controls.subScope.hasError('required')) {
                <small class="text-danger">O subâmbito é obrigatório.</small>
              }
              @if (form.controls.subScope.errors?.['server']; as serverMessage) {
                <small class="text-danger">{{ serverMessage }}</small>
              }
            </div>
          }

          @if (showIntegratedSystems()) {
            <div class="col-12">
              <label class="form-label d-block">Sistemas aplicáveis <span class="text-muted small">(selecione dois ou mais)</span></label>
              @for (system of allSystems; track system) {
                <div class="form-check form-check-inline">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    [id]="'system-' + system"
                    [checked]="isSystemSelected(system)"
                    (change)="toggleSystem(system, $any($event.target).checked)"
                  />
                  <label class="form-check-label" [for]="'system-' + system">{{ systemLabels[system] }}</label>
                </div>
              }
              @if (systemsError()) { <div><small class="text-danger">{{ systemsError() }}</small></div> }
            </div>
          }

          @if (showSubScopeOther()) {
            <div class="col-md-6">
              <label class="form-label">Identificar o sistema</label>
              <input class="form-control" formControlName="subScopeOther" placeholder="Qual o sistema?" />
            </div>
          }

          <!-- A origem vem antes do tipo desde o documento de 2026-10-05: a lista é uma só,
               do âmbito, e deixou de depender do tipo e da natureza. -->
          <div class="col-md-6">
            <label class="form-label">Origem <span class="text-danger">*</span></label>
            <select class="form-select" formControlName="origin">
              <option [ngValue]="null" disabled>Selecione…</option>
              <!-- Anónima só entra pelo site (documento de 2026-10-05): fora dele há sempre
                   alguém do outro lado, e é a origem que o diz. -->
              @for (option of originOptions(); track option.value) {
                <option [ngValue]="option.value" [disabled]="anonymous() && option.value !== 'SITE'">
                  {{ option.label }}@if (anonymous() && option.value !== 'SITE') { (só o site admite anónimo) }
                </option>
              }
            </select>
            @if (parentId() !== null) {
              <small class="text-muted">Fixa enquanto estiver ligada à ocorrência de origem.</small>
            }
            @if (form.controls.origin.touched && form.controls.origin.hasError('required')) {
              <small class="text-danger">A origem é obrigatória.</small>
            }
            @if (form.controls.origin.errors?.['server']; as serverMessage) {
              <small class="text-danger">{{ serverMessage }}</small>
            }
          </div>

          @if (showOriginDetail()) {
            <div class="col-md-6">
              <label class="form-label">Detalhe da origem</label>
              <select class="form-select" formControlName="originDetail">
                <option [ngValue]="null" disabled>Selecione…</option>
                @for (option of originDetailOptions(); track option.value) { <option [ngValue]="option.value">{{ option.label }}</option> }
              </select>
              @if (form.controls.originDetail.touched && form.controls.originDetail.hasError('required')) {
                <small class="text-danger">Indique o detalhe da origem.</small>
              }
              @if (form.controls.originDetail.errors?.['server']; as serverMessage) {
                <small class="text-danger">{{ serverMessage }}</small>
              }
            </div>
          }

          @if (showInterestedParty()) {
            <div class="col-md-6">
              <label class="form-label">Parte interessada avaliada</label>
              <select class="form-select" formControlName="interestedParty" data-no-filter>
                <option [ngValue]="null" disabled>Selecione…</option>
                @for (option of interestedPartyOptions; track option.value) {
                  <option [ngValue]="option.value">{{ option.label }}</option>
                }
              </select>
              @if (form.controls.interestedParty.touched && form.controls.interestedParty.hasError('required')) {
                <small class="text-danger">Indique a parte interessada avaliada.</small>
              }
              @if (form.controls.interestedParty.errors?.['server']; as serverMessage) {
                <small class="text-danger">{{ serverMessage }}</small>
              }
            </div>
          }

          @if (showOriginOther()) {
            <div class="col-md-6">
              <label class="form-label">{{ originOtherLabel() }}</label>
              <input class="form-control" formControlName="originOther" [placeholder]="originOtherLabel()" />
            </div>
          }

          <div class="col-md-6">
            <label class="form-label">Tipo de ocorrência <span class="text-danger">*</span></label>
            <select class="form-select" formControlName="type">
              <option [ngValue]="null" disabled>
                {{ typeOptions().length ? 'Selecione…' : 'Selecione primeiro o âmbito e o subâmbito' }}
              </option>
              @for (option of typeOptions(); track option.value) { <option [ngValue]="option.value">{{ option.label }}</option> }
            </select>
            @if (form.controls.type.touched && form.controls.type.hasError('required')) {
              <small class="text-danger">O tipo é obrigatório.</small>
            }
            @if (form.controls.type.errors?.['server']; as serverMessage) {
              <small class="text-danger">{{ serverMessage }}</small>
            }
          </div>

          @if (showTypeOther()) {
            <div class="col-md-6">
              <label class="form-label">Identificar outro tipo</label>
              <input class="form-control" formControlName="typeOther" placeholder="Descreva o tipo" />
            </div>
          }

          <!-- A natureza é o que separa uma reclamação de uma denúncia ou de uma ideia, e
               comanda o canal, o motivo e as categorias que aparecem a seguir. -->
          @if (showNature()) {
            <div class="col-md-6">
              <label class="form-label">Natureza</label>
              <select class="form-select" formControlName="nature">
                <option [ngValue]="null" disabled>Selecione…</option>
                @for (option of natureOptions(); track option.value) {
                  <option [ngValue]="option.value">{{ option.label }}</option>
                }
              </select>
              @if (form.controls.nature.touched && form.controls.nature.hasError('required')) {
                <small class="text-danger">Indique a natureza da participação.</small>
              }
              @if (form.controls.nature.hasError('subScope')) {
                <small class="text-danger">Não são consideradas reclamações para este subâmbito.</small>
              }
              @if (form.controls.nature.errors?.['server']; as serverMessage) {
                <small class="text-danger">{{ serverMessage }}</small>
              }
            </div>
          }

          @if (isComplaint()) {
            <div class="col-md-6">
              <label class="form-label">Motivo da reclamação</label>
              <select class="form-select" formControlName="complaintReason" data-no-filter>
                <option [ngValue]="null" disabled>Selecione…</option>
                @for (option of reasonOptions(); track option.value) {
                  <option [ngValue]="option.value">{{ option.label }}</option>
                }
              </select>
              @if (form.controls.complaintReason.touched && form.controls.complaintReason.hasError('required')) {
                <small class="text-danger">Indique o motivo da reclamação.</small>
              }
              @if (form.controls.complaintReason.errors?.['server']; as serverMessage) {
                <small class="text-danger">{{ serverMessage }}</small>
              }
            </div>
            @if (showComplaintOther()) {
              <div class="col-md-6">
                <label class="form-label">Identificar o motivo</label>
                <input class="form-control" formControlName="complaintReasonOther" placeholder="Qual o motivo?" />
              </div>
              <div class="col-12">
                <div class="alert alert-warning d-flex mb-0" role="note">
                  <i class="fe fe-alert-triangle me-2 mt-1"></i>
                  <span>
                    Com outro motivo, a reclamação entra identificada. Podes reclassificá-la
                    para anónima se o caso o justificar.
                  </span>
                </div>
              </div>
            }
            <div class="col-12">
              <div class="form-check">
                <input class="form-check-input" type="checkbox" id="recurringComplaint"
                       formControlName="recurringComplaint" />
                <label class="form-check-label" for="recurringComplaint">Reclamação recorrente</label>
              </div>
            </div>
          }

          <!-- Um campo só (documento de 2026-10-08): as "Categorias associadas ao caso" e a
               "Natureza do incidente ou violação" pediam o mesmo em dois sítios da mesma
               secção. A lista é a do âmbito; na Segurança da Informação são as naturezas do
               incidente. -->
          @if (showIncidentField()) {
            <div class="col-12">
              <label class="form-label d-block">
                Natureza do incidente ou violação <span class="text-muted small">(seleção múltipla)</span>
              </label>
              <div class="row g-1">
                @if (showIncidentNatures()) {
                  @for (option of incidentNatureOptions; track option.value) {
                    <div class="col-md-6">
                      <div class="form-check">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          [id]="'nature-' + option.value"
                          [checked]="isNatureSelected(option.value)"
                          (change)="toggleIncidentNature(option.value, $any($event.target).checked)"
                        />
                        <label class="form-check-label" [for]="'nature-' + option.value">{{ option.label }}</label>
                      </div>
                    </div>
                  }
                } @else {
                  @for (option of categoryOptions(); track option.value) {
                    <div class="col-md-6">
                      <div class="form-check">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          [id]="'category-' + option.value"
                          [checked]="isReportCategorySelected(option.value)"
                          (change)="toggleReportCategory(option.value, $any($event.target).checked)"
                        />
                        <label class="form-check-label" [for]="'category-' + option.value">{{ option.label }}</label>
                      </div>
                    </div>
                  }
                }
              </div>
              @if (categoriesError()) { <small class="text-danger">{{ categoriesError() }}</small> }
            </div>
            @if (reportRequiresIdentification()) {
              <div class="col-12">
                <div class="alert alert-warning d-flex mb-0" role="note">
                  <i class="fe fe-alert-triangle me-2 mt-1"></i>
                  <span>
                    Nas denúncias de proteção de dados a identificação é obrigatória: o caso tem de
                    se poder relacionar com o titular dos dados.
                  </span>
                </div>
              </div>
            }
            @if (showReportCategoryOther()) {
              <div class="col-md-6">
                <label class="form-label">Identificar a outra irregularidade <span class="text-danger">*</span></label>
                <input class="form-control" formControlName="reportCategoryOther" placeholder="Qual?" />
              </div>
              <div class="col-12">
                <div class="alert alert-warning d-flex mb-0" role="note">
                  <i class="fe fe-alert-triangle me-2 mt-1"></i>
                  <span>
                    Nesta categoria a identificação é obrigatória: a denúncia não pode ser anónima.
                  </span>
                </div>
              </div>
            }
          }

          @if (showHealthDataNote()) {
            <div class="col-12">
              <div class="alert alert-info d-flex mb-0" role="note">
                <i class="fe fe-info me-2 mt-1"></i>
                <span>{{ healthDataNote }}</span>
              </div>
            </div>
          }

          @if (showProcessArea()) {
            <div class="col-md-6">
              <label class="form-label">
                Processo / área
                @if (isComplaint()) { <span class="text-danger">*</span> }
                @else { }
              </label>
              <select class="form-select" formControlName="processArea">
                <option [ngValue]="null">—</option>
                @for (option of processAreaOptions; track option.value) {
                  <option [ngValue]="option.value">{{ option.label }}</option>
                }
              </select>
              @if (form.controls.processArea.touched && form.controls.processArea.hasError('required')) {
                <small class="text-danger">Indique o processo ou área.</small>
              }
              @if (form.controls.processArea.errors?.['server']; as serverMessage) {
                <small class="text-danger">{{ serverMessage }}</small>
              }
            </div>
            @if (showProcessAreaOther()) {
              <div class="col-md-6">
                <label class="form-label">Identificar o processo ou área</label>
                <input class="form-control" formControlName="processAreaOther" placeholder="Qual?" />
              </div>
            }
          }


          @if (criticalCaseId() !== null && !isEdit()) {
            <div class="col-12">
              @if (criticalCaseError()) {
                <div class="alert alert-danger d-flex align-items-center gap-2 mb-0 py-2">
                  <i class="fe fe-alert-octagon"></i>
                  <span>Caso crítico #{{ criticalCaseId() }}: {{ criticalCaseError() }}</span>
                </div>
              } @else {
                <div class="alert alert-info d-flex align-items-center gap-2 mb-0 py-2">
                  <i class="fe fe-alert-octagon"></i>
                  <span>Ligada ao caso crítico <strong>#{{ criticalCaseId() }}</strong>@if (criticalCaseTarget()) { — {{ criticalCaseTarget() }} } — a ligação fica permanente.</span>
                </div>
              }
            </div>
          }
          @if (parentCode()) {
            <div class="col-12">
              <div class="alert alert-info d-flex align-items-center gap-2 mb-0 py-2">
                <i class="fe fe-link"></i>
                <span>Derivada da ocorrência <strong>{{ parentCode() }}</strong> — a ligação é permanente e ficará visível nos dois registos.</span>
              </div>
            </div>
          }
          @if (orphan()) {
            <div class="col-12">
              <div class="alert alert-light border d-flex align-items-center gap-2 mb-0 py-2">
                <i class="fe fe-link-2"></i>
                <span>
                  A ocorrência de origem foi eliminada e a ligação quebrou-se. O histórico desta
                  guarda de onde veio.
                </span>
              </div>
            </div>
          }
          @if (replicaOf()) {
            <div class="col-12">
              <div class="alert alert-light border d-flex align-items-center gap-2 mb-0 py-2">
                <i class="fe fe-copy"></i>
                <span>
                  Réplica de <strong>{{ replicaOf() }}</strong>: veio a classificação, a origem e a
                  descrição. Não fica ligada a ela — reveja o que for diferente antes de registar.
                </span>
              </div>
            </div>
          }

          @if (showParentPicker()) {
            <div class="col-md-6">
              <label class="form-label">Ocorrência de origem <span class="text-danger">*</span></label>
              <select class="form-select" formControlName="parentSelect">
                <option [ngValue]="null" disabled>Selecione a ocorrência-mãe…</option>
                @for (candidate of parentCandidates(); track candidate.id) {
                  <option [ngValue]="candidate.id">{{ candidate.label }}</option>
                }
              </select>
              @if (!parentCandidates().length) {
                <!-- Lista vazia é um beco sem saída silencioso: a ligação é obrigatória e não há
                     nada para escolher. Diz-se porquê e qual é a saída. -->
                <small class="text-danger">
                  Não há outra ocorrência a que ligar esta. Escolha outro detalhe da origem.
                </small>
              }
              @if (form.controls.parentSelect.touched && form.controls.parentSelect.hasError('required')) {
                <small class="text-danger">Uma ação de resposta exige a ligação à ocorrência de origem.</small>
              }
              @if (form.controls.parentSelect.errors?.['server']; as serverMessage) {
                <small class="text-danger">{{ serverMessage }}</small>
              }
            </div>
          }

          @if (showParticipant()) {
            <div class="col-12"><hr class="my-1" /></div>
            <div class="col-md-6">
              <label class="form-label">Identificação do participante</label>
              <select class="form-select" formControlName="participantIdentification" data-no-filter>
                <option [ngValue]="null" disabled>Selecione…</option>
                @for (option of participantOptions; track option.value) {
                  <!-- O anónimo fica sempre à vista: desativado, e com o motivo, quando não se
                       admite (revisão de 2026-10-15). -->
                  <option [ngValue]="option.value"
                          [disabled]="option.value === 'ANONIMO' && anonymousBlockedReason() !== null">
                    {{ option.label }}@if (option.value === 'ANONIMO' && anonymousBlockedReason(); as reason) { ({{ reason }}) }
                  </option>
                }
              </select>
              @if (identificationLocked()) {
                <small class="text-muted">Este motivo obriga a participação identificada.</small>
              }
              @if (form.controls.participantIdentification.touched && form.controls.participantIdentification.hasError('required')) {
                <small class="text-danger">Indique a identificação do participante.</small>
              }
              @if (form.controls.participantIdentification.errors?.['server']; as serverMessage) {
                <small class="text-danger">{{ serverMessage }}</small>
              }
            </div>
            @if (showParticipantContacts()) {
              <div class="col-md-3">
                <label class="form-label">Nome do participante</label>
                <app-client-picker
                  formControlName="participantClientId"
                  [initialName]="form.controls.participantName.value"
                  [initialEmail]="form.controls.participantEmail.value"
                  [currentEmail]="form.controls.participantEmail.value"
                  (nameChange)="form.controls.participantName.setValue($event)"
                  (emailChange)="form.controls.participantEmail.setValue($event)" />
              </div>
              <div class="col-md-3">
                <label class="form-label">E-mail</label>
                <input class="form-control" type="email" formControlName="participantEmail" />
              </div>
              <div class="col-md-3">
                <label class="form-label">Telefone</label>
                <input class="form-control" formControlName="participantPhone" />
              </div>
              <div class="col-md-3">
                <label class="form-label">Relação com a organização</label>
                <select class="form-select" formControlName="participantRelation">
                  <option [ngValue]="null">—</option>
                  @for (option of relationOptions; track option.value) {
                    <option [ngValue]="option.value">{{ option.label }}</option>
                  }
                </select>
              </div>
              @if (showWorkerType()) {
                <div class="col-md-3">
                  <label class="form-label">Tipo de colaborador</label>
                  <select class="form-select" formControlName="participantWorkerType" data-no-filter>
                    <option [ngValue]="null">—</option>
                    @for (option of workerTypeOptions; track option.value) {
                      <option [ngValue]="option.value">{{ option.label }}</option>
                    }
                  </select>
                </div>
              }
              <div class="col-12">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" id="participantConfidential"
                         formControlName="participantConfidential" />
                  <label class="form-check-label" for="participantConfidential">
                    Necessidade de reforçar a confidencialidade
                  </label>
                </div>
              </div>
            }
          }

          <!-- O bloco "Titular dos dados" saiu (revisão de 2026-10-15): quem comunica um pedido
               de exercício de direitos é o titular. O pisco da identidade validada passou para a
               Análise; os campos antigos continuam gravados e na ficha. -->
          @if (showTargetedPersons() || showConflictParties()) {
            <div class="col-12"><hr class="my-1" /></div>
          }
          @if (showTargetedPersons()) {
            <div class="col-md-6">
              <label class="form-label">Pessoas ou áreas visadas</label>
              <input class="form-control" formControlName="targetedPersons" />
            </div>
          }
          @if (showConflictParties()) {
            <div class="col-md-6">
              <label class="form-label">Conflito de interesses — tipo/partes envolvidas</label>
              <input class="form-control" formControlName="conflictParties" />
            </div>
          }
        </div>
      </div>
    </div>
      }
      @if (step() === 'descricao') {
    <div class="card">
      <div class="card-header">
        <div class="d-flex align-items-center">
          <span class="avatar avatar-sm me-3"><span class="avatar-title fs-lg rounded bg-warning-soft text-warning"><i class="fe fe-file-text"></i></span></span>
          <h4 class="card-header-title mb-0">Descrição</h4>
        </div>
        <span class="badge bg-danger-soft">Obrigatória</span>
      </div>
      <div class="card-body">
        <div class="row g-3">

          <div class="col-12">
            <label class="form-label">Título <span class="text-danger">*</span></label>
            <!-- Área de texto: os títulos das constatações de auditoria são frases inteiras, e numa
                 linha só ficavam cortados à vista (2026-10-16). Sem maxlength, para um título colado
                 não ser cortado em silêncio — passa do limite e diz-se. -->
            <textarea class="form-control" rows="2" formControlName="title"
                      placeholder="Resumo da ocorrência"></textarea>
            @if (form.controls.title.touched && form.controls.title.hasError('required')) {
              <small class="text-danger">O título é obrigatório.</small>
            }
            @if (form.controls.title.hasError('maxlength')) {
              <small class="text-danger">
                O título tem no máximo {{ titleMaxLength }} caracteres — tem {{ form.controls.title.value.length }}.
              </small>
            } @else if (form.controls.title.value.length > titleMaxLength - 50) {
              <small class="text-muted">{{ form.controls.title.value.length }} / {{ titleMaxLength }} caracteres</small>
            }
            @if (form.controls.title.errors?.['server']; as serverMessage) {
              <small class="text-danger">{{ serverMessage }}</small>
            }
          </div>

          <div class="col-12">
            <!-- Opcional desde a revisão de 2026-10-19. -->
            <label class="form-label">Descrição</label>
            <textarea class="form-control" formControlName="description" rows="2"></textarea>
          </div>

          <div class="col-md-4">
            <label class="form-label">Data da ocorrência <span class="text-danger">*</span></label>
            <app-date-picker formControlName="occurrenceDate" [max]="today" />
            @if (form.controls.occurrenceDate.touched && form.controls.occurrenceDate.hasError('required')) {
              <small class="text-danger">A data da ocorrência é obrigatória.</small>
            }
            @if (form.controls.occurrenceDate.errors?.['server']; as serverMessage) {
              <small class="text-danger">{{ serverMessage }}</small>
            }
          </div>

          <!--
            Os prazos pertencem às secções Tratamento e Verificação da eficácia — mas ao criar
            essas secções ainda não existem. Em vez de campos que mudam de sítio depois, aqui vão
            calculados e em leitura, com a indicação de onde se editam.
          -->
          @if (!isEdit()) {
            <div class="col-12">
              <div class="alert alert-light border d-flex align-items-start gap-2 mb-0">
                <i class="fe fe-clock mt-1 text-muted"></i>
                <div class="small">
                  <strong>Prazos automáticos.</strong>
                  @if (form.controls.actionImplementationDeadline.value) {
                    Implementação (+6 meses):
                    <strong>{{ form.controls.actionImplementationDeadline.value | date: 'dd/MM/yyyy' }}</strong>
                    · Avaliação da eficácia (+8 meses):
                    <strong>{{ form.controls.effectivenessEvaluationDeadline.value | date: 'dd/MM/yyyy' }}</strong>.
                  } @else {
                    Implementação (+6 meses) e avaliação da eficácia (+8 meses) calculam-se a partir da
                    <em>data da ocorrência</em>.
                  }
                  <br />Depois de gravar, ajusta-os na edição, nas secções <em>Tratamento</em> e <em>Verificação da eficácia</em>.
                </div>
              </div>
            </div>
          }
        </div>

      </div>
    </div>
      }
      @if (step() === 'analise') {
      <div class="card">
        <div class="card-header">
          <div class="d-flex align-items-center">
            <span class="avatar avatar-sm me-3"><span class="avatar-title fs-lg rounded bg-info-soft text-info"><i class="fe fe-search"></i></span></span>
            <h4 class="card-header-title mb-0">Análise</h4>
          </div>
          @if (requiredFrom('EM_TRATAMENTO')) {
            <span class="badge bg-danger-soft">Obrigatória para concluir a análise</span>
          }
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label">
                Análise inicial <span class="text-muted small">(ocorrências não graves, ex.: OP)</span>
              </label>
              <textarea class="form-control" formControlName="initialAnalysis" rows="3"></textarea>
              @if (form.controls.initialAnalysis.touched && form.controls.initialAnalysis.hasError('required')) {
                <small class="text-danger">Preencha a análise inicial ou a análise de causas.</small>
              }
              @if (form.controls.initialAnalysis.errors?.['server']; as serverMessage) {
                <small class="text-danger">{{ serverMessage }}</small>
              }
            </div>
            <div class="col-md-6">
              <label class="form-label">
                Análise das causas
                @if (causeRequired()) { <span class="text-danger">*</span> }
              </label>
              <textarea class="form-control" formControlName="causeAnalysis" rows="3"></textarea>
              <small class="form-text text-muted">
                Obrigatória nas não conformidades, ações corretivas, reclamações e denúncias.
              </small>
              @if (form.controls.causeAnalysis.touched && form.controls.causeAnalysis.hasError('required')) {
                <small class="text-danger d-block">
                  Preencha a análise de causas: é obrigatória nas não conformidades, ações corretivas,
                  reclamações e denúncias.
                </small>
              }
              @if (form.controls.causeAnalysis.errors?.['server']; as serverMessage) {
                <small class="text-danger d-block">{{ serverMessage }}</small>
              }
            </div>
            @if (isCaseWork()) {
              <div class="col-12"><hr class="my-1" /></div>
              <div class="col-12">
                <h5 class="mb-1">Triagem</h5>
                <p class="text-muted small mb-0">
                  Mostra que o caso foi triado e em que dia.
                  @if (form.controls.triageDecisionDeadline.value) {
                    A decisão tem prazo até {{ form.controls.triageDecisionDeadline.value | date: 'dd/MM/yyyy' }}
                    (cinco dias úteis a contar da receção).
                  }
                </p>
              </div>
              <div class="col-md-4">
                <label class="form-label">
                  Resultado da triagem <span class="text-danger">*</span>
                </label>
                <select class="form-select" formControlName="triageResult" data-no-filter>
                  <option [ngValue]="null">—</option>
                  @for (option of triageResultOptions; track option.value) {
                    <option [ngValue]="option.value">{{ option.label }}</option>
                  }
                </select>
                @if (form.controls.triageResult.touched && form.controls.triageResult.hasError('required')) {
                  <small class="text-danger">Indique o resultado da triagem: aceite ou não aceite.</small>
                }
                @if (form.controls.triageResult.errors?.['server']; as serverMessage) {
                  <small class="text-danger">{{ serverMessage }}</small>
                }
              </div>
              <div class="col-md-4">
                <label class="form-label">
                  Data da triagem <span class="text-danger">*</span>
                </label>
                <app-date-picker
                  formControlName="triageDecidedAt"
                  [min]="form.controls.occurrenceDate.value"
                  [max]="today"
                />
                @if (form.controls.triageDecidedAt.touched && form.controls.triageDecidedAt.hasError('required')) {
                  <small class="text-danger">Indique a data da triagem.</small>
                }
                @if (form.controls.triageDecidedAt.errors?.['server']; as serverMessage) {
                  <small class="text-danger">{{ serverMessage }}</small>
                }
              </div>
              <div class="col-md-4">
                <label class="form-label">
                  Prazo para decidir <span class="text-muted small">(auto +5 dias úteis)</span>
                </label>
                <app-date-picker
                  formControlName="triageDecisionDeadline"
                  [min]="form.controls.occurrenceDate.value"
                />
              </div>
            }
            <!-- "Adicionar um pisco: Requer elementos adicionais" (documento de 2026-10-03).
                 Vivia no Tratamento, mas é na análise que se percebe que falta alguma coisa para
                 decidir — e é a triagem que fica à espera. De qualquer ocorrência desde a revisão
                 de 2026-10-19: era só das denúncias e reclamações. -->
            <div class="col-12"><hr class="my-1" /></div>
            <div class="col-12">
              <div class="form-check">
                <input class="form-check-input" type="checkbox" id="awaiting-elements"
                       formControlName="awaitingElements" />
                <label class="form-check-label" for="awaiting-elements">
                  Necessidade de elementos adicionais
                </label>
              </div>
              <small class="text-muted d-block">
                Explica, mais tarde, porque é que o caso esteve parado. Sem resposta, pode ser
                encerrado por insuficiência de elementos, com o fundamento registado e sem
                prejuízo de reabertura.
              </small>
            </div>
            @if (form.controls.awaitingElements.value) {
              <div class="col-12">
                <span class="text-muted small">Estado da triagem:</span>
                <span class="badge bg-warning-soft ms-1">Aguarda elementos</span>
              </div>
              <!-- Pedir agora ou registar que já se pediu (documento de 2026-10-08): é o que
                   decide de onde vem a data do pedido. -->
              <div class="col-12">
                <div class="form-check form-check-inline">
                  <input class="form-check-input" type="radio" id="elements-ask"
                         name="elementsMode" [checked]="elementsMode() === 'PEDIR'"
                         (change)="setElementsMode('PEDIR')" />
                  <label class="form-check-label" for="elements-ask">Pedir?</label>
                </div>
                <div class="form-check form-check-inline">
                  <input class="form-check-input" type="radio" id="elements-asked"
                         name="elementsMode" [checked]="elementsMode() === 'JA_SOLICITADO'"
                         (change)="setElementsMode('JA_SOLICITADO')" />
                  <label class="form-check-label" for="elements-asked">Já solicitado</label>
                </div>
                @if (elementsMode() === 'PEDIR') {
                  @if (isEdit()) {
                    @if (!elementsEmail()) {
                      <button type="button" class="btn btn-sm btn-white ms-2"
                              (click)="openElementsEmail()">
                        <i class="fe fe-mail me-1"></i> Enviar e-mail
                      </button>
                    }
                  } @else {
                    <span class="small text-muted ms-2">
                      O pedido envia-se depois de a ocorrência estar gravada.
                    </span>
                  }
                }
              </div>
              @if (elementsMode() === 'PEDIR' && elementsEmail()) {
                <div class="col-12">
                  <div class="border rounded p-3">
                    <div class="row g-2">
                      <div class="col-md-6">
                        <label class="form-label">Para</label>
                        <input class="form-control" type="email" [(ngModel)]="elementsTo"
                               [ngModelOptions]="{ standalone: true }"
                               placeholder="endereco@exemplo.pt" />
                      </div>
                      <div class="col-md-6">
                        <label class="form-label">Assunto</label>
                        <input class="form-control" [(ngModel)]="elementsSubject"
                               [ngModelOptions]="{ standalone: true }" />
                      </div>
                      <div class="col-12">
                        <label class="form-label">Mensagem</label>
                        <textarea class="form-control" rows="3" [(ngModel)]="elementsBody"
                                  [ngModelOptions]="{ standalone: true }"></textarea>
                      </div>
                    </div>
                    <div class="d-flex justify-content-end gap-2 mt-2">
                      <button type="button" class="btn btn-sm btn-white" (click)="cancelElementsEmail()">Cancelar</button>
                      <button type="button" class="btn btn-sm btn-primary" (click)="sendElementsEmail()"
                              [disabled]="sendingElements()">
                        {{ sendingElements() ? 'A enviar…' : 'Enviar' }}
                      </button>
                    </div>
                  </div>
                </div>
              }
              <div class="col-md-6">
                <label class="form-label">Data do pedido</label>
                <app-date-picker
                  formControlName="elementsRequestedAt"
                  [min]="form.controls.occurrenceDate.value"
                  [max]="today"
                />
                <small class="form-text text-muted">
                  @if (elementsMode() === 'PEDIR') {
                    Preenche-se sozinha com a data do envio.
                  } @else {
                    Escreva a data em que os elementos foram pedidos.
                  }
                </small>
              </div>
              <div class="col-md-6">
                <label class="form-label">
                  Prazo para responder <span class="text-muted small">(auto +15 dias do pedido)</span>
                </label>
                <app-date-picker
                  formControlName="elementsResponseDeadline"
                  [min]="form.controls.elementsRequestedAt.value"
                />
              </div>
            }
            <!-- Recusar na triagem fecha a ocorrência, e fechar exige dizer em que deu. Os
               campos aparecem aqui, onde a decisão é tomada, em vez de mandarem a pessoa à
               secção da identificação à procura deles. -->
            @if (stageCloses()) {
              <div class="col-12">
              <div class="alert alert-warning d-flex align-items-start gap-2 mb-0">
                <i class="fe fe-alert-triangle mt-1"></i>
                <span>
                  Com a triagem <strong>não aceite</strong>, concluir a análise fecha a
                  ocorrência — tipo de fecho <strong>{{ closingTypeLabels['FECHADO_APOS_ANALISE'] }}</strong>.
                </span>
              </div>
              </div>
              <!-- Primeiro o desfecho, depois o resultado — que só é obrigatório quando o
                   desfecho é "Outro" (revisão de 2026-10-19). -->
              <div class="col-md-6">
              <label class="form-label">Desfecho <span class="text-danger">*</span></label>
              <select class="form-select" formControlName="outcome" data-no-filter>
                <option [ngValue]="null" disabled>Selecione…</option>
                @for (option of outcomeOptions; track option.value) {
                  <option [ngValue]="option.value">{{ option.label }}</option>
                }
              </select>
              @if (form.controls.outcome.touched && form.controls.outcome.hasError('required')) {
                <small class="text-danger">Escolha o desfecho do caso.</small>
              }
              </div>
              <div class="col-12">
              <label class="form-label">
                Resultado @if (closingResultRequired()) { <span class="text-danger">*</span> }
              </label>
              <textarea class="form-control" formControlName="closingResult" rows="2"
                        placeholder="O que ficou apurado ao fechar a ocorrência."></textarea>
              @if (form.controls.closingResult.touched && form.controls.closingResult.hasError('required')) {
                <small class="text-danger">Descreva o resultado com que a ocorrência foi fechada.</small>
              }
              </div>
            }
            <div class="col-12"><hr class="my-1" /></div>
            <!-- Impacto e requisito vieram da secção Avaliação, que deixou de existir
                 (documento de 2026-10-03). Graduar o caso é parte de o analisar: é na análise
                 que se percebe o que aconteceu, e era aí que a marca de obrigatório já estava. -->
            <div class="col-md-4">
              <label class="form-label">
                Impacto
                <!-- Marcado pelo tipo, como a análise de causas: é o que concluir a análise exige.
                     Preso também ao estado, aparecia nuns casos e noutros não (revisão de 2026-10-15). -->
                @if (graded()) { <span class="text-danger">*</span> }
              </label>
              <select class="form-select" formControlName="impact" data-no-filter>
                <option [ngValue]="null">—</option>
                @for (option of impactOptions; track option.value) {
                  <option [ngValue]="option.value">{{ option.label }}</option>
                }
              </select>
              @if (form.controls.impact.touched && form.controls.impact.hasError('required')) {
                <small class="text-danger">Indique o impacto da ocorrência.</small>
              }
              @if (form.controls.impact.errors?.['server']; as serverMessage) {
                <small class="text-danger">{{ serverMessage }}</small>
              }
            </div>
            <div class="col-md-8">
              <label class="form-label">
                Requisito
                <!-- Marcado como o impacto, sem "(opcional)" do outro lado: os dois são exigidos nas
                     mesmas ocorrências e dispensados nas mesmas (revisão de 2026-10-19). -->
                @if (requirementRequired()) { <span class="text-danger">*</span> }
              </label>
              <input class="form-control" formControlName="requirementReference"
                     placeholder="Cláusula, requisito legal ou normativo aplicável" />
              @if (form.controls.requirementReference.touched && form.controls.requirementReference.hasError('required')) {
                <small class="text-danger">Indique o requisito aplicável.</small>
              }
              @if (form.controls.requirementReference.errors?.['server']; as serverMessage) {
                <small class="text-danger">{{ serverMessage }}</small>
              }
            </div>
            <!-- Exercício de direitos: validar a identidade de quem pede é parte de analisar o
                 pedido, e é a conclusão da análise que o exige (revisão de 2026-10-15). Vivia num
                 bloco "Titular dos dados" da Caracterização, mas quem comunica é o titular. -->
            @if (showDataSubject()) {
              <div class="col-12"><hr class="my-1" /></div>
              <div class="col-12">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" id="dataSubjectValidated" formControlName="dataSubjectValidated" />
                  <label class="form-check-label" for="dataSubjectValidated">
                    Identidade do titular dos dados validada <span class="text-danger">*</span>
                  </label>
                </div>
                <small class="text-muted d-block">O pedido só segue para tratamento depois de validada a identidade de quem o fez.</small>
                @if (form.controls.dataSubjectValidated.errors?.['server']; as serverMessage) {
                  <small class="text-danger d-block">{{ serverMessage }}</small>
                }
              </div>
            }
            <!-- Veio da Descrição (documento de 2026-10-08): decidir se há eficácia para
                 verificar, e até quando, é parte de analisar a ocorrência. -->
            <div class="col-12"><hr class="my-1" /></div>
            <div class="col-12">
              <div class="form-check">
                <input class="form-check-input" type="checkbox" id="effectiveness-applicable"
                       [checked]="effectivenessApplicable()"
                       (change)="setEffectivenessApplicable($any($event.target).checked)" />
                <label class="form-check-label" for="effectiveness-applicable">
                  Verificação da eficácia aplicável?
                </label>
              </div>
              <small class="text-muted d-block">
                Sem ela, a avaliação fica <strong>Não aplicável</strong> e os restantes campos da
                secção <em>Verificação da eficácia</em> ficam em branco.
              </small>
            </div>
            @if (effectivenessApplicable()) {
              <div class="col-md-4">
                <label class="form-label">Prazo p/ aval. da eficácia</label>
                <app-date-picker
                  formControlName="effectivenessEvaluationDeadline"
                  [min]="form.controls.actionImplementationDeadline.value"
                />
                <small class="form-text text-muted">Automático: +8 meses do prazo de implementação.</small>
              </div>
            }
          </div>
        </div>
      </div>
      }
      @if (step() === 'tratamento') {
      <div class="card">
        <div class="card-header">
          <div class="d-flex align-items-center">
            <span class="avatar avatar-sm me-3"><span class="avatar-title fs-lg rounded bg-primary-soft text-primary"><i class="fe fe-zap"></i></span></span>
            <h4 class="card-header-title mb-0">Tratamento</h4>
          </div>
          @if (requiredFrom('EM_MONITORIZACAO')) {
            <span class="badge bg-danger-soft">Obrigatória para passar a "{{ stateLabels['EM_MONITORIZACAO'] }}"</span>
          }
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-12">
              <!-- As ações deixaram de ser um campo de texto com um responsável para tudo
                   (documento de 2026-10-03): é uma lista, cada uma com o seu número e o seu
                   responsável, e o seguimento diz a qual se refere. -->
              @if (isEdit()) {
                <app-occurrence-actions
                  [occurrenceId]="id()!"
                  [teamMembers]="teamMembers()"
                  (changed)="refreshStepMarksFromChild()"
                />
              } @else {
                <p class="text-muted mb-0">
                  As ações do tratamento registam-se depois de a ocorrência existir — cada uma com
                  o seu responsável.
                </p>
              }
            </div>
            <div class="col-md-6">
              <label class="form-label">
                Prazo de implementação
                @if (requiredFrom('EM_MONITORIZACAO')) { <span class="text-danger">*</span> }
              </label>
              <app-date-picker
                formControlName="actionImplementationDeadline"
                [min]="form.controls.occurrenceDate.value"
              />
              <small class="form-text text-muted">Automático: +6 meses da data da ocorrência.</small>
              @if (form.controls.actionImplementationDeadline.touched && form.controls.actionImplementationDeadline.hasError('required')) {
                <small class="text-danger">Indique o prazo de implementação.</small>
              }
              @if (form.controls.actionImplementationDeadline.errors?.['server']; as serverMessage) {
                <small class="text-danger">{{ serverMessage }}</small>
              }
            </div>
            <!-- O prazo de informar é das naturezas que têm comunicante — inclui as
                 sugestões/ideias. A espera de elementos é só da denúncia e da reclamação. -->
            @if (hasCommunicant()) {
              <div class="col-12"><hr class="my-1" /></div>
              <div class="col-md-6">
                <label class="form-label">
                  Prazo para informar quem participou <span class="text-muted small">(auto +30 dias da receção)</span>
                </label>
                <app-date-picker
                  formControlName="informDeadline"
                  [min]="form.controls.occurrenceDate.value"
                />
                <small class="text-muted d-block">
                  Se a análise não estiver concluída, substitua por nova data prevista: o aviso
                  volta a tocar dois dias antes dela.
                </small>
              </div>
            }
          </div>
        </div>
      </div>
      }
      @if (isEdit() && step() === 'monitorizacao') {
      <div class="card">
        <div class="card-header">
          <div class="d-flex align-items-center">
            <span class="avatar avatar-sm me-3"><span class="avatar-title fs-lg rounded bg-secondary-soft text-secondary"><i class="fe fe-activity"></i></span></span>
            <h4 class="card-header-title mb-0">Seguimento</h4>
          </div>
          <!-- O seguimento escreve-se durante o tratamento; é a entrada daqui que deixa
               passar a Em avaliação. -->
          @if (requiredFrom('AVALIACAO_REAVALIACAO')) {
            <span class="badge bg-danger-soft">Obrigatória para passar a "{{ stateLabels['AVALIACAO_REAVALIACAO'] }}"</span>
          } @else if (canRegisterFollowUp()) {
            <span class="badge bg-warning-soft">Regista-se durante o tratamento</span>
          }
        </div>
        <div class="card-body">
          <!-- O mesmo componente do cartão da ficha: o estado exige seguimento aqui, por isso
               tem de se poder registar aqui. -->
          <app-occurrence-follow-ups
            [occurrenceId]="id()!"
            [teamMembers]="teamMembers()"
            [canRegister]="canRegisterFollowUp()"
          />
        </div>
      </div>
      }
      @if (step() === 'eficacia') {
      <div class="card">
        <div class="card-header">
          <div class="d-flex align-items-center">
            <span class="avatar avatar-sm me-3"><span class="avatar-title fs-lg rounded bg-success-soft text-success"><i class="fe fe-award"></i></span></span>
            <h4 class="card-header-title mb-0">Verificação da eficácia</h4>
          </div>
        </div>
        <div class="card-body">
          <div class="row g-3">
            @if (!effectivenessApplicable()) {
              <div class="col-12">
                <div class="alert alert-light border d-flex align-items-start gap-2 mb-0">
                  <i class="fe fe-info mt-1 text-muted"></i>
                  <div class="small">
                    A verificação da eficácia foi dada como <strong>não aplicável</strong> na
                    Análise. Os restantes campos ficam em branco — para os preencher, volte à
                    Análise e ligue o pisco.
                  </div>
                </div>
              </div>
            }
            <div class="col-md-4">
              <label class="form-label">
                Resultado da avaliação
              </label>
              <select class="form-select" formControlName="effectivenessEvaluation" data-no-filter>
                <option [ngValue]="null">—</option>
                <option [ngValue]="'NAO_APLICAVEL'">Não aplicável</option>
                <option [ngValue]="'EFICAZ'">Eficaz</option>
                <option [ngValue]="'NAO_EFICAZ'">Não eficaz</option>
                <option [ngValue]="'NAO_FOI_POSSIVEL_AFERIR'">Não foi possível de aferir</option>
              </select>
              @if (form.controls.effectivenessEvaluation.touched && form.controls.effectivenessEvaluation.hasError('required')) {
                <small class="text-danger">Indique o resultado da avaliação.</small>
              }
              @if (form.controls.effectivenessEvaluation.errors?.['server']; as serverMessage) {
                <small class="text-danger">{{ serverMessage }}</small>
              }
            </div>
            <div class="col-md-4">
              <label class="form-label">
                Data da avaliação da eficácia
              </label>
              <app-date-picker
                formControlName="effectivenessEvaluationDate"
                [min]="form.controls.occurrenceDate.value"
                [max]="today"
              />
              @if (form.controls.effectivenessEvaluationDate.touched && form.controls.effectivenessEvaluationDate.hasError('required')) {
                <small class="text-danger">Indique a data da avaliação.</small>
              }
              @if (form.controls.effectivenessEvaluationDate.errors?.['server']; as serverMessage) {
                <small class="text-danger">{{ serverMessage }}</small>
              }
            </div>
            @if (effectivenessApplicable() && form.controls.effectivenessEvaluationDeadline.value) {
              <div class="col-12">
                <small class="text-muted">
                  Prazo p/ aval. da eficácia (na Análise):
                  <strong>{{ form.controls.effectivenessEvaluationDeadline.value | date: 'dd/MM/yyyy' }}</strong>
                </small>
              </div>
            }
            <div class="col-12">
              <label class="form-label">
                Constatações da avaliação de eficácia
              </label>
              <textarea class="form-control" formControlName="effectivenessFindings" rows="3"></textarea>
              @if (form.controls.effectivenessFindings.touched && form.controls.effectivenessFindings.hasError('required')) {
                <small class="text-danger">Descreva as constatações da avaliação.</small>
              }
              @if (form.controls.effectivenessFindings.errors?.['server']; as serverMessage) {
                <small class="text-danger">{{ serverMessage }}</small>
              }
            </div>
            @if (showUnableReason()) {
              <div class="col-12">
                <label class="form-label">Motivo <span class="text-danger">*</span></label>
                <textarea class="form-control" formControlName="effectivenessUnableReason" rows="2"
                          placeholder="Por que não foi possível aferir a eficácia?"></textarea>
                @if (form.controls.effectivenessUnableReason.touched && form.controls.effectivenessUnableReason.hasError('required')) {
                  <small class="text-danger">Indique por que não foi possível aferir.</small>
                }
                @if (form.controls.effectivenessUnableReason.errors?.['server']; as serverMessage) {
                  <small class="text-danger">{{ serverMessage }}</small>
                }
              </div>
            }
          </div>
        </div>
      </div>
      }
      @if (isEdit() && step() === 'evidencias') {
      <div class="card">
        <div class="card-header">
          <div class="d-flex align-items-center">
            <span class="avatar avatar-sm me-3"><span class="avatar-title fs-lg rounded bg-warning-soft text-warning"><i class="fe fe-paperclip"></i></span></span>
            <h4 class="card-header-title mb-0">Evidências</h4>
          </div>
          <span class="badge bg-secondary-soft">Opcional</span>
        </div>
        <div class="card-body">
          <!-- O mesmo componente do cartão da ficha: registar, corrigir e anexar sem fechar o
               painel (revisão de 2026-10-13). -->
          <app-occurrence-evidences [occurrenceId]="id()!" />
        </div>
      </div>
      }
      </div>
    </div>

    <footer class="occ-form-foot">
      @if (serverError()) {
        <div class="alert alert-danger mb-0 py-2 flex-grow-1">
          <div>{{ serverError() }}</div>
          @if (serverFieldErrors().length) {
            <!-- O que o servidor recusou, em concreto. Os campos ficam vermelhos, mas o motivo
                 ("não pode ser anterior a…") não cabe debaixo de cada um. -->
            <ul class="mb-0 mt-2 ps-3">
              @for (message of serverFieldErrors(); track message) {
                <li>{{ message }}</li>
              }
            </ul>
          }
        </div>
      }
      @if (hasBlockingErrors()) {
        <div class="alert alert-warning mb-0 py-2 flex-grow-1">
          <i class="fe fe-alert-triangle me-1"></i>
          Há campos por preencher nos separadores assinalados a vermelho.
        </div>
      }
      <!-- A triagem decide para onde a análise concluída leva a ocorrência. Aqui no rodapé, ao
           lado dos botões: em painel à parte empurrava-os para fora do ecrã. -->
      @if (triageChoice()) {
        <div class="alert alert-info mb-0 py-2 flex-grow-1">
          <strong>Como ficou a triagem?</strong>
          <span class="d-block small">
            Aceite leva a ocorrência a "{{ stateLabels['EM_TRATAMENTO'] }}". Não aceite fecha-a,
            com o tipo de fecho "{{ closingTypeLabels['FECHADO_APOS_ANALISE'] }}" — e ainda há o
            desfecho para escolher aqui na Análise (e o resultado, se o desfecho for "Outro").
          </span>
        </div>
      }
      <span class="occ-form-foot-spacer"></span>
      @if (triageChoice()) {
        <!-- Escolher a triagem é uma decisão só: o resto dos botões sai da frente. -->
        <button class="btn btn-white text-nowrap" type="button" (click)="cancelTriageChoice()">Voltar</button>
        <button class="btn btn-white text-nowrap" type="button" [disabled]="loading()"
                (click)="concludeAnalysis(false)">
          <i class="fe fe-x me-1"></i>Não aceite
        </button>
        <button class="btn btn-primary text-nowrap" type="button" [disabled]="loading()"
                (click)="concludeAnalysis(true)">
          <i class="fe fe-check me-1"></i>Aceite
        </button>
      } @else {
        <!-- Também na edição (revisão de 2026-10-15): volta ao que estava por baixo — a lista,
             quando se abriu de lá — e pergunta antes se há alterações por gravar. -->
        <button class="btn btn-white" type="button" (click)="requestClose()">Cancelar</button>
        @if (!isFirstStep()) {
          <button class="btn btn-white" type="button" (click)="previousStep()">
            <i class="fe fe-chevron-left me-1"></i>Anterior
          </button>
        }
        @if (!isLastStep()) {
          <button class="btn btn-white" type="button" (click)="nextStep()">
            Seguinte<i class="fe fe-chevron-right ms-1"></i>
          </button>
        }
        <!-- O "Guardar" está em todas as secções (documento de 2026-10-06). Ao lado dele, a
             secção oferece o botão que põe a ocorrência a andar: entrar no estado desta secção,
             concluir a análise, ou fechar quando a triagem recusou. -->
        <button class="btn" [class.btn-primary]="!stageButton()" [class.btn-white]="stageButton()"
                [disabled]="loading()">
          {{ loading() ? 'A guardar…' : 'Guardar' }}
        </button>
        @if (startAction(); as start) {
          <button class="btn btn-primary" type="button" [disabled]="loading()" (click)="startStage()">
            {{ loading() ? 'A guardar…' : start.label }}
          </button>
        }
        @if (canConcludeAnalysis() && !stageCloses()) {
          <button class="btn btn-primary" type="button" [disabled]="loading()" (click)="openTriageChoice()">
            Concluir análise
          </button>
        }
        @if (canConcludeTreatment()) {
          <button class="btn btn-primary text-nowrap" type="button" [disabled]="loading()"
                  (click)="concludeTreatment()">
            {{ loading() ? 'A guardar…' : 'Concluir tratamento' }}
          </button>
        }
        @if (stageCloses() && originalSituation() !== 'FECHADO') {
          <button class="btn btn-white text-nowrap" type="button" [disabled]="loading()"
                  (click)="undoTriageChoice()">
            <i class="fe fe-corner-up-left me-1"></i>Voltar à triagem
          </button>
          <button class="btn btn-primary" type="button" [disabled]="loading()" (click)="submit()">
            {{ loading() ? 'A guardar…' : 'Fechar ocorrência' }}
          </button>
        }
      }
    </footer>
  </form>

  <!-- Gravada a descrição com a ocorrência ainda em Registado: o passo seguinte é a análise,
       e perguntá-lo aqui poupa ir procurá-lo à ficha. -->
  @if (askStartAnalysis() !== null) {
    <app-confirm-dialog
      title="Iniciar a análise?"
      message="A ocorrência passa a Em análise e a secção abre para preencher. Se agora não, fica em Registado e pode começar mais tarde."
      confirmLabel="Iniciar análise"
      cancelLabel="Agora não"
      (confirm)="confirmStartAnalysis()"
      (cancel)="declineStartAnalysis()"
    />
  }

  @if (pendingClose()) {
    <app-confirm-dialog
      title="Sair sem guardar?"
      message="O que escreveu desde a última gravação perde-se."
      confirmLabel="Sair sem guardar"
      cancelLabel="Continuar a preencher"
      [danger]="true"
      (confirm)="confirmClose()"
      (cancel)="cancelClose()"
    />
  }

  @if (pendingScope()) {
    <app-confirm-dialog
      title="Mudar o âmbito?"
      message="O subâmbito, o tipo e os campos que dependem deles são limpos."
      confirmLabel="Mudar âmbito"
      (confirm)="confirmScopeChange()"
      (cancel)="cancelScopeChange()"
    />
  }
</div>
</dialog>
